import get from 'lodash.get';

import IdempotencyKeyService from '../../services/idempotencyKeyService';
import DonationService from '../../services/donationService';

import update from '../../redux/update';
import { isArrayWithItems } from '../../helpers/arrayHelper';
import { getQuestionsFromModel } from '../../helpers/formBuilderHelper';

import { displayNameKeys, displayNameOptions } from '../../constants/displayNameOptions';
import { loadingStatus } from '../../constants/loading';
import { tabs } from './constants/tabs';
import { donationPaymentType } from '../../constants/donationTypeOptions';
import { FieldCategoryKey } from '../../constants/formbuilder';

import { GETTING_PAGE_SUCCESS } from '../shared/reducer';
import RecurringService from '../../services/recurringService';

import { AnalyticsHelper } from "../../helpers/analyticsHelper";

export const VALUE_CHANGE = 'donation/VALUE_CHANGE';
export const CLEAR_DONOR_ADDITIONAL_INFO = 'donation/CLEAR_DONOR_ADDITIONAL_INFO';
export const ON_DONATION_TYPE_CHANGE = 'donation/ON_DONATION_TYPE_CHANGE';
export const DEFAULT_SELECTION_CHANGE = "donation/DEFAULT_SELECTION_CHANGE";
export const CUSTOM_AMOUNT_SELECT = "donations/CUSTOM_AMOUNT_SELECT";
export const SET_PAYMENT_STARTED = "donation/SET_PAYMENT_STARTED";
export const MAKE_PAYMENT = "donation/MAKE_PAYMENT";
export const MAKE_PAYMENT_STARTED = "donation/MAKE_PAYMENT_STARTED";
export const MAKE_PAYMENT_ERROR = "donation/MAKE_PAYMENT_ERROR";
export const CARD_ELEMENT_CHANGE = 'donation/CARD_ELEMENT_CHANGE';
export const DIPLAY_NAME_OPTION_CHANGE = 'donation/DIPLAY_NAME_OPTION_CHANGE';
export const MAKE_PAYMENT_ERROR_DISMISS = "donation/MAKE_PAYMENT_ERROR_DISMISS";
export const COVER_PROCESSING_FEE_TOGGLE = 'donation/COVER_PROCESSING_FEE_TOGGLE';
export const FETCH_PAYMENT_FEE_STARTED = 'donation/FETCH_PAYMENT_FEE_STARTED';
export const FETCH_PAYMENT_FEE = 'donation/FETCH_PAYMENT_FEE';
export const PAYMENT_FEE_REMOVE = 'donation/PAYMENT_FEE_REMOVE';
export const PAYMENT_CLEAR = 'donation/PAYMENT_CLEAR';

const initialState = {
    selectedDefaultAmountIndex: null,
    amount: null,
    amountText: 'Donation',
    customAmountSelected: false,
    activePage: "AMOUNT",
    leaveSupportMessage: true,
    selectedDisplayNameOption: displayNameKeys.asFirstName,
    displayName: '',
    message: '',
    customer: {
        firstName: '',
        lastName: '',
        email: '',
        businessName: '',
        anonymous: false,
        newsletter: true
    },
    subscription: {
        enabled: false,
        frequency: '',
        donationLimitCount: null
    },
    tabsValidation: {
        [tabs.DETAILS]: false
    },
    paymentInProgress: loadingStatus.NOT_STARTED,
    serverErrorOccured: false,
    serverErrorKey: null,
    coverProcessingFee: true,
    isUserAgreedTerms: false,
    cardErrorMessage: "",
    cardComplete: false,
    canMakePayment: false,
    paymentRequest: null,
    fetchingFeeInProgress: false,
    paymentRequestAmount: 0,
    totalFees: 0,
    idempotencyKey: IdempotencyKeyService.getIdempotencyKey(),
    giftMatching: {
        activeIndex: 0
    },
    paymentType: donationPaymentType.creditCard.value
}

const getSelectedItem = list => {
    if (list) {
        const item = list.find(item => item.isDefault === true);
        if (item) {
            return {
                index: list.indexOf(item) + 1,
                item: item
            }
        }
    }
    return null;
}

const donationReducer = (state = initialState, action) => {
    let newState = state;
    switch (action.type) {
        case GETTING_PAGE_SUCCESS: {
            const defaultAmounts = get(action.payload, "campaign.template.value.donationSetup.defaultAmounts");
            const selected = getSelectedItem(defaultAmounts);

            const baseQuestionsModel = action.payload.campaign;

            const hideCoverFee = get(action.payload, "campaign.template.value.donationSetup.hideCoverFee");
            const coverProcessingFee = hideCoverFee ? false : true;

            const newsletter = get(action.payload, "campaign.template.value.customSetup.newsletter");
            const defaultNewsletterValue = newsletter && isArrayWithItems(newsletter.pages) && newsletter.pages.some(item => item === "donation") ? newsletter.defaultValue : true;

            newState = {
                ...state,
                formbuilderAnswers: getQuestionsFromModel(baseQuestionsModel, FieldCategoryKey.DONATION),
                selectedDefaultAmountIndex: get(selected, "index"),
                amount: get(selected, "item.amount"),
                defaultAmounts: defaultAmounts,
                donationText: get(selected, "item.description"),
                hideCoverFee: hideCoverFee,
                coverProcessingFee: coverProcessingFee,
                customer: {
                    ...state.customer,
                    newsletter: defaultNewsletterValue
                },
                giftMatching: action.payload?.campaign?.giftMatching
            }
            break;
        }

        case ON_DONATION_TYPE_CHANGE: {
            const selected = getSelectedItem(action.payload.defaultAmounts);

            const selectedDefaultAmountIndex = selected ? selected.index : 0;
            const amount = selected ? selected.item.amount : 0;
            const donationText = selected ? selected.item.description : "Other donation";
            const customAmountSelected = selected ? false : true;

            newState = {
                ...state,
                subscription: {
                    ...state.subscription,
                    enabled: action.payload.enabled
                },
                defaultAmounts: action.payload.defaultAmounts,
                selectedDefaultAmountIndex: selectedDefaultAmountIndex,
                amount: amount,
                donationText: donationText,
                customAmountSelected: customAmountSelected,
                paymentType: donationPaymentType.creditCard.value
            }

            break;
        }

        case VALUE_CHANGE:
            newState = update.set(newState, action.payload.path, action.payload.value);
            break;

        case DEFAULT_SELECTION_CHANGE:
            newState = {
                ...state,
                selectedDefaultAmountIndex: action.payload.index,
                amount: action.payload.amount,
                donationText: action.payload.donationText,
                customAmountSelected: false
            }
            break;

        case CUSTOM_AMOUNT_SELECT:
            newState = {
                ...state,
                selectedDefaultAmountIndex: 0,
                donationText: "Other donation",
                customAmountSelected: true
            }
            break;

        case SET_PAYMENT_STARTED:
            newState = update.set(newState, "paymentInProgress", loadingStatus.IS_LOADING);
            break;

        case MAKE_PAYMENT_STARTED:
            newState = update(state, {
                payment: { $set: action.payload.donation }
            });
            break;

        case MAKE_PAYMENT:
            newState = update(state, {
                paymentInProgress: { $set: loadingStatus.SUCCESS },
                createdPaymentRecord: { $set: action.payload.data }
            });
            break;

        case DIPLAY_NAME_OPTION_CHANGE: {
            const diplayNameOption = displayNameOptions.find(x => x.key === action.payload.value);

            newState = update(newState, {
                selectedDisplayNameOption: { $set: diplayNameOption.key },
                customer: {
                    anonymous: { $set: diplayNameOption.isAnonymous }
                }
            });
            break;
        }

        case MAKE_PAYMENT_ERROR: {
            const errorArray = get(action.payload, "response.data.error");
            const serverErrorKey = isArrayWithItems(errorArray) ? errorArray[0].error : null;

            newState = update(state, {
                paymentInProgress: { $set: loadingStatus.ERROR },
                serverErrorOccured: { $set: true },
                serverErrorKey: { $set: serverErrorKey },
                idempotencyKey: { $set: IdempotencyKeyService.getIdempotencyKey() }
            });
            break;
        }

        case CARD_ELEMENT_CHANGE:
            newState = update(state, {
                cardComplete: { $set: action.payload.cardComplete },
                cardErrorMessage: { $set: action.payload.cardErrorMessage }
            });
            break;

        case MAKE_PAYMENT_ERROR_DISMISS:
            newState = update(state, {
                serverErrorOccured: { $set: false },
                serverErrorKey: { $set: null }
            });
            break;

        case CLEAR_DONOR_ADDITIONAL_INFO:
            newState = update(newState, {
                selectedDisplayNameOption: { $set: displayNameKeys.asFirstName },
                displayName: { $set: '' },
                message: { $set: '' },
                customer: {
                    anonymous: { $set: false }
                }
            });
            break;

        case COVER_PROCESSING_FEE_TOGGLE:
            newState = update(state, {
                coverProcessingFee: { $set: action.payload.coverFee }
            });
            break;

        case PAYMENT_FEE_REMOVE:
            newState = update(state, {
                paymentRequestAmount: { $set: state.amount }
            });
            break;

        case FETCH_PAYMENT_FEE_STARTED:
            newState = update(state, {
                fetchingFeeInProgress: { $set: true }
            });
            break;

        case FETCH_PAYMENT_FEE:
            newState = update(state, {
                fetchingFeeInProgress: { $set: false },
                paymentRequestAmount: { $set: action.payload.data.totalWithFees },
                totalFees: { $set: action.payload.data.totalFees }
            });
            break;

        case PAYMENT_CLEAR:
            newState = update(state, {
                cardErrorMessage: { $set: "" },
                cardComplete: { $set: false },
            });
            break;

        default: return newState;
    }

    return newState;
}

export const onValueChange = (path, value) => {
    return {
        type: VALUE_CHANGE,
        payload: {
            path: path,
            value: value
        }
    }
}

export const onDonationTypeChange = isRecurring => {
    return (dispatch, getState) => {
        const state = getState();
        const campaignDefaultAmounts = get(state, 'page.campaign.defaultDonations');
        const organizationDefaultAmounts = get(state, 'page.organization.defaultDonations');

        dispatch({
            type: ON_DONATION_TYPE_CHANGE,
            payload: {
                enabled: isRecurring,
                defaultAmounts: isRecurring ? organizationDefaultAmounts : campaignDefaultAmounts
            }
        });
    }
}

export const clearDonorAdditionalInfo = () => {
    return {
        type: CLEAR_DONOR_ADDITIONAL_INFO
    }
}

export const onChangeDefaultSelection = (index, amount, donationText) => {
    return {
        type: DEFAULT_SELECTION_CHANGE,
        payload: {
            index: index,
            amount: amount,
            donationText: donationText
        }
    }
}

export const displayNameOptionChange = value => {
    return {
        type: DIPLAY_NAME_OPTION_CHANGE,
        payload: {
            value: value
        }
    }
}

export const selectCustomAmount = () => {
    return {
        type: CUSTOM_AMOUNT_SELECT
    }
}

export const paymentClear = () => {
    return {
        type: PAYMENT_CLEAR,
        payload: {}
    }
}

export const makePaymentRequest = (donation, idempotencyKey, verificationToken) => {
    return (dispatch) => {
        dispatch(makePaymentStarted(donation));
        DonationService.makePayment(donation, idempotencyKey, verificationToken)
            .then((response) => {
                dispatch(makePayment(response.data));
            })
            .catch((error) => {
                dispatch(makePaymentErrorOccured(error));
            });
    }
}

export const makePaymentIntentPublic = (guid, verificationToken, walletInfoMessage) => {
    return (dispatch, getState) => {
        const state = getState();
        const campaignName = get(state, 'page.campaign.name');
        
        DonationService.getDonationIntentPublic(guid, IdempotencyKeyService.getIdempotencyKey(), verificationToken)
            .then((response) => {
                dispatch(makePayment({
                    ...response.data,
                    data: {
                        ...response.data.data,
                        walletInfoMessage
                    }
                }));
            })
            .catch(() => {
                if (window) {
                    AnalyticsHelper.createEvent(campaignName, 'DonationPaymentFailed', window.location.pathname, 0, 0);
                }

                dispatch(makePayment({
                    walletInfoMessage
                }));
            });
    }
}

export const makeRecurringPaymentRequest = (donation, idempotencyKey, verificationToken) => {
    return (dispatch) => {
        dispatch(makePaymentStarted(donation));
        RecurringService.createNewDonation(donation, idempotencyKey, verificationToken)
            .then((response) => {
                dispatch(makePayment(response.data));
            })
            .catch((error) => {
                dispatch(makePaymentErrorOccured(error));
            });
    }
}

export const makeBankAccountPaymentRequest = (donation, idempotencyKey, confirmCallback, verificationToken) => {
    return (dispatch) => {
        dispatch(makePaymentStarted(donation));
        DonationService.makePaymentV3(donation, idempotencyKey, verificationToken)
            .then(response => {
                confirmCallback(get(response, 'data.data.financials.intentPublicKey'))
                    .then(() => {
                        dispatch(makePayment(response.data));
                    })
                    .catch((error) => {
                        dispatch(makePaymentErrorOccured(error));
                    });
            })
            .catch((error) => {
                dispatch(makePaymentErrorOccured(error));
            });
    }
}

export const makeRecurringBankAccountPaymentRequest = (donation, idempotencyKey, confirmCallback, verificationToken) => {
    return (dispatch) => {
        dispatch(makePaymentStarted(donation));
        RecurringService.createNewDonationV3(donation, idempotencyKey, verificationToken)
            .then(response => {
                confirmCallback(get(response, 'data.data.payment.intentPublicKey'))
                    .then(() => {
                        dispatch(makePayment(response.data));
                    })
                    .catch((error) => {
                        dispatch(makePaymentErrorOccured(error));
                    });
            })
            .catch((error) => {
                dispatch(makePaymentErrorOccured(error));
            });
    }
}

export const makeRecurringCardIntentRequest = (model, idempotencyKey, confirmCallback, verificationToken) => {
    return (dispatch) => {
        dispatch(makePaymentStarted(model));
        RecurringService.createNewDonationIntent(model, idempotencyKey, verificationToken)
            .then(response => {
                confirmCallback(get(response, 'data.data.financials.intentPublicKey'))
                    .then(confirmResult => {
                        if (confirmResult && confirmResult.error) {
                            dispatch(makePaymentErrorOccured(confirmResult.error));
                        } else {
                            dispatch(makePayment(response.data));
                        }
                    })
                    .catch((error) => {
                        dispatch(makePaymentErrorOccured(error));
                    });
            })
            .catch((error) => {
                dispatch(makePaymentErrorOccured(error));
            });
    }
}

export const makePaymentFeeRequest = (orgPath, campaignPath, amount) => {
    return (dispatch) => {
        dispatch(fetchingPaymentFeeStarted());
        DonationService.getPaymentFee(orgPath, campaignPath, amount)
            .then((response) => {
                dispatch(fetchPaymentFee(response.data));
            })
            .catch(() => {
            });
    }
}

export const dismissPaymentError = () => {
    return {
        type: MAKE_PAYMENT_ERROR_DISMISS,
        payload: {}
    };
}

export const cardElementChange = card => {
    return {
        type: CARD_ELEMENT_CHANGE,
        payload: {
            cardComplete: card.complete,
            cardErrorMessage: card.error ? card.error.message : null,
        }
    }
}

export const coverProcessingFeeToggle = (coverFee) => {
    return {
        type: COVER_PROCESSING_FEE_TOGGLE,
        payload: {
            coverFee
        }
    }
}

export const setPaymentRequestAmountWithNoFee = () => {
    return {
        type: PAYMENT_FEE_REMOVE,
        payload: {}
    }
}

export const setPaymentStarted = () => {
    return {
        type: SET_PAYMENT_STARTED,
        payload: {}
    };
}

export const makePaymentErrorOccured = (error) => {
    return {
        type: MAKE_PAYMENT_ERROR,
        payload: error
    };
}

const makePayment = (data) => {
    return {
        type: MAKE_PAYMENT,
        payload: data
    };
}

const makePaymentStarted = donation => {
    return {
        type: MAKE_PAYMENT_STARTED,
        payload: { donation }
    };
}

const fetchingPaymentFeeStarted = () => {
    return {
        type: FETCH_PAYMENT_FEE_STARTED,
        payload: {}
    }
}

const fetchPaymentFee = (data) => {
    return {
        type: FETCH_PAYMENT_FEE,
        payload: data
    }
}

export const recalculatePaymentFee = (orgPath, campaignPath, amount, coverFee) => {
    return (dispatch) => {
        if (coverFee) {
            dispatch(fetchingPaymentFeeStarted());
            DonationService.getPaymentFee(orgPath, campaignPath, amount)
                .then((response) => {
                    dispatch(fetchPaymentFee(response.data));
                })
                .catch(() => {
                });
        } else {
            dispatch(setPaymentRequestAmountWithNoFee())
        }
    }
}

export const recalculateRecurringPaymentFee = (orgPath, amount, coverFee) => {
    return (dispatch) => {
        if (coverFee) {
            dispatch(fetchingPaymentFeeStarted());
            RecurringService.getPaymentFee(orgPath, amount)
                .then((response) => {
                    dispatch(fetchPaymentFee(response.data));
                })
                .catch(() => {
                });
        } else {
            dispatch(setPaymentRequestAmountWithNoFee())
        }
    }
}

export default donationReducer;